import { useState } from "react";
import {
  ArrowUp,
  ArrowDown,
  Minus,
  Scales,
  Brain,
  Database,
  Info,
  FileText,
  CaretRight,
} from "@phosphor-icons/react";
import { Badge } from "../ui/badge";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";
import { getImpactColor as getImpactFillColor } from "../../lib/styleHelpers";

const CHART_MARGIN = { left: 80, right: 20, top: 5, bottom: 5 };
const BAR_RADIUS = [0, 4, 4, 0];
const TICK_STYLE_DARK = { fontSize: 11, fill: '#6b7280' };
const TICK_STYLE_LIGHT = { fontSize: 11, fill: '#9ca3af' };
const TOOLTIP_STYLE_DARK = {
  background: "#12121a",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: "8px",
  fontSize: "12px",
  color: "#fff"
};
const WEIGHT_FORMATTER = (value) => [`${value}%`, "Weight"];

function getImpactIcon(impact) {
  switch (impact) {
    case "positive": return <ArrowUp size={14} className="text-emerald-400" />;
    case "negative": return <ArrowDown size={14} className="text-red-400" />;
    default: return <Minus size={14} className="text-gray-500" />;
  }
}

function getImpactColor(impact) {
  switch (impact) {
    case "positive": return "bg-emerald-500/10 border-emerald-500/30";
    case "negative": return "bg-red-500/10 border-red-500/30";
    default: return "bg-gray-500/10 border-gray-500/30";
  }
}

function getConfidenceLevelColor(level) {
  switch (level) {
    case "Very High": return "text-emerald-400 bg-emerald-500/10 border-emerald-500/30";
    case "High": return "text-purple-400 bg-purple-500/10 border-purple-500/30";
    case "Moderate": return "text-amber-400 bg-amber-500/10 border-amber-500/30";
    default: return "text-red-400 bg-red-500/10 border-red-500/30";
  }
}

const X_AXIS_DOMAIN = [0, 30];

export function ConfidenceBreakdownCard({ breakdown }) {
  const [expanded, setExpanded] = useState(false);

  const chartData = breakdown.drivers?.map(d => ({
    name: d.factor.length > 15 ? d.factor.substring(0, 15) + "..." : d.factor,
    weight: d.weight,
    fill: getImpactFillColor(d.impact)
  })) || [];

  return (
    <div className="mb-6 border border-purple-500/30 bg-purple-500/5 rounded-xl overflow-hidden" data-testid="confidence-breakdown">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-purple-500/10 transition-colors"
        data-testid="confidence-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
            <Brain size={20} className="text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-bold text-white flex items-center gap-2">
              Confidence Score Explanation
              <Badge className={getConfidenceLevelColor(breakdown.confidence_level)}>
                {breakdown.confidence_level}
              </Badge>
            </h3>
            <p className="text-sm text-gray-400 font-body">
              See what data points and indicators drove the {Math.round(breakdown.overall_score * 100)}% probability score
            </p>
          </div>
        </div>
        <CaretRight size={20} className={`text-gray-500 transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>
      
      {expanded && (
        <div className="border-t border-purple-500/30 p-6 animate-fade-in bg-[#0c0c16]">
          {/* Score Composition Chart */}
          <div className="mb-6">
            <h4 className="font-bold text-white mb-4 flex items-center gap-2">
              <Scales size={18} className="text-purple-400" />
              Score Composition
            </h4>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1f1f2e" horizontal={true} vertical={false} />
                  <XAxis type="number" domain={X_AXIS_DOMAIN} tick={TICK_STYLE_DARK} stroke="#374151" />
                  <YAxis type="category" dataKey="name" tick={TICK_STYLE_LIGHT} stroke="#374151" width={80} />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE_DARK}
                    formatter={WEIGHT_FORMATTER}
                  />
                  <Bar dataKey="weight" radius={BAR_RADIUS}>
                    {chartData.map((entry) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          {/* Driver Details */}
          <div className="mb-6">
            <h4 className="font-bold text-white mb-4 flex items-center gap-2">
              <Database size={18} className="text-purple-400" />
              Factor Analysis
            </h4>
            <div className="space-y-3">
              {breakdown.drivers?.map((driver) => (
                <div 
                  key={driver.factor} 
                  className={`p-4 rounded-lg border ${getImpactColor(driver.impact)}`}
                  data-testid={`driver-${driver.factor}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getImpactIcon(driver.impact)}
                      <span className="font-bold text-slate-900 text-sm">{driver.factor}</span>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {driver.weight}% weight
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 font-body mb-2">{driver.explanation}</p>
                  <div className="flex items-center gap-2 text-xs bg-white/80 px-3 py-2 rounded border border-slate-100">
                    <Info size={14} className="text-blue-700 flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{driver.data_point}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Methodology */}
          {breakdown.methodology && (
            <div className="mb-6">
              <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                <FileText size={18} className="text-blue-700" />
                Methodology
              </h4>
              <p className="text-sm text-slate-600 font-body bg-slate-50 p-3 rounded border border-slate-200">
                {breakdown.methodology}
              </p>
            </div>
          )}
          
          {/* Key Assumptions */}
          {breakdown.key_assumptions?.length > 0 && (
            <div className="mb-6">
              <h4 className="font-bold text-slate-900 mb-2 text-sm">Key Assumptions</h4>
              <ul className="space-y-1">
                {breakdown.key_assumptions.map((assumption) => (
                  <li key={`assume-${assumption.slice(0, 25)}`} className="flex items-start gap-2 text-sm text-slate-600 font-body">
                    <span className="text-slate-400">&#8226;</span>
                    {assumption}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Validation Sources */}
          {breakdown.validation_sources?.length > 0 && (
            <div>
              <h4 className="font-bold text-slate-900 mb-2 text-sm">Validation Sources</h4>
              <div className="flex flex-wrap gap-2">
                {breakdown.validation_sources.map((source) => (
                  <Badge key={source} variant="outline" className="text-xs font-body bg-white">
                    {source}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
